import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
/**
 * DrawerContext — centralized body overflow management for drawers.
 *
 * Problem:
 * When each Drawer instance independently manages `document.body.style.overflow`,
 * race conditions occur during React Suspense transitions. For example, a Suspense
 * fallback renders a skeleton drawer (which sets overflow: hidden), then the real
 * content mounts a new drawer — but the fallback's cleanup fires *after* the new
 * drawer mounts, resetting overflow to '' while a drawer is still visible.
 * This causes the body scrollbar to flash back momentarily.
 *
 * Solution:
 * A ref-counted context tracks how many non-stacked drawers are currently open.
 * - When the count goes from 0 → 1, body overflow is set to 'hidden'.
 * - When the count drops back to 0, body overflow is restored.
 * - Stacked drawers (child panels) are excluded from the count since the parent
 *   drawer already controls overflow.
 *
 * This ensures that even if a Suspense boundary swaps a fallback drawer for a real
 * one (unmount + mount in the same tick), the count stays ≥ 1 and overflow remains
 * hidden throughout the transition.
 */

interface DrawerContextValue {
    register: () => void;
    unregister: () => void;
}

const DrawerContext = createContext<DrawerContextValue | null>(null);

export const DrawerProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const countRef = useRef(0);

    const register = useCallback(() => {
        countRef.current++;
        if (countRef.current === 1) {
            document.body.style.overflow = 'hidden';
        }
    }, []);

    const unregister = useCallback(() => {
        countRef.current--;
        if (countRef.current <= 0) {
            countRef.current = 0;
            document.body.style.overflow = '';
        }
    }, []);

    return <DrawerContext.Provider value={{ register, unregister }}>{children}</DrawerContext.Provider>;
};

export const useDrawerOverflow = (isOpen: boolean, isStacked: boolean) => {
    const ctx = useContext(DrawerContext);

    useEffect(() => {
        if (isStacked || !ctx) return;
        if (isOpen) {
            ctx.register();
            return () => ctx.unregister();
        }
    }, [isOpen, isStacked, ctx]);
};
